/**
 * BulkImportService — CSV-based bulk product import.
 *
 * Why CSV-only: avoids pulling in xlsx (large dep) for v1. Excel & Sheets
 * both export clean .csv. We parse RFC-4180 style (quoted strings, doubled
 * quotes, CRLF), validate per-row, and write in chunked atomic batches.
 *
 * Columns (case-insensitive header match):
 *   name*           - product name (required)
 *   sku             - stock keeping unit
 *   barcode
 *   hsn / hsn_code
 *   category        - matched against existing Category.name (created if missing)
 *   unit            - default 'pcs'
 *   selling_price*  - number, > 0
 *   purchase_price  - number, default 0
 *   mrp             - number, default selling_price
 *   gst_rate        - number, default 0
 *   stock           - number, default 0
 *   low_stock_alert - number, default 0
 *   track_inventory - 'true'|'false', default true
 *
 * `validateRows` returns the parsed + validated rows ready for write;
 * `commitRows` performs the writes in 200-row atomic chunks so very
 * large imports don't stall the DB lock for too long.
 */

import { Q } from '@nozbe/watermelondb';
import { database, txn } from '@/db';
import { Product, Category } from '@/db/models/Inventory';
import { logActivity } from '@/utils/auditLog';
import { useSettingsStore } from '@/store/useSettingsStore';
import { toStoredSellingPrice } from '@/billing/priceMode';

// ─── CSV parser ───────────────────────────────────────────────────────────
export function parseCsv(text: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let cur = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') { cur += '"'; i++; }
        else { inQuotes = false; }
      } else {
        cur += ch;
      }
    } else {
      if (ch === '"') inQuotes = true;
      else if (ch === ',') { row.push(cur); cur = ''; }
      else if (ch === '\r') { /* swallow */ }
      else if (ch === '\n') { row.push(cur); rows.push(row); row = []; cur = ''; }
      else cur += ch;
    }
  }
  if (cur.length > 0 || row.length > 0) { row.push(cur); rows.push(row); }
  // Strip trailing empty rows
  while (rows.length > 0 && rows[rows.length - 1].every((c) => c.trim() === '')) rows.pop();
  return rows;
}

// ─── Column mapping ───────────────────────────────────────────────────────
const SYNONYMS: Record<string, string> = {
  name: 'name',
  product: 'name',
  product_name: 'name',
  sku: 'sku',
  code: 'sku',
  barcode: 'barcode',
  ean: 'barcode',
  hsn: 'hsn_code',
  hsn_code: 'hsn_code',
  category: 'category',
  unit: 'unit',
  uom: 'unit',
  selling_price: 'selling_price',
  price: 'selling_price',
  mrp: 'mrp',
  purchase_price: 'purchase_price',
  cost: 'purchase_price',
  gst: 'gst_rate',
  gst_rate: 'gst_rate',
  tax: 'gst_rate',
  price_includes_gst: 'price_includes_gst',
  price_incl_gst: 'price_includes_gst',
  with_gst: 'price_includes_gst',
  gst_inclusive: 'price_includes_gst',
  inclusive: 'price_includes_gst',
  stock: 'stock',
  qty: 'stock',
  quantity: 'stock',
  current_stock: 'stock',
  low_stock_alert: 'low_stock_alert',
  reorder_level: 'low_stock_alert',
  track_inventory: 'track_inventory',
};

function normalizeHeader(h: string): string {
  return h.trim().toLowerCase().replace(/[\s\-]+/g, '_');
}

export function mapHeaders(headers: string[]): Record<string, number> {
  const map: Record<string, number> = {};
  headers.forEach((raw, idx) => {
    const norm = normalizeHeader(raw);
    const canonical = SYNONYMS[norm];
    if (canonical && map[canonical] === undefined) map[canonical] = idx;
  });
  return map;
}

// ─── Validation ───────────────────────────────────────────────────────────
export interface ValidatedRow {
  name: string;
  sku: string | null;
  barcode: string | null;
  hsnCode: string | null;
  categoryName: string | null;
  unit: string;
  sellingPrice: number;
  purchasePrice: number;
  mrp: number;
  gstRate: number;
  currentStock: number;
  lowStockAlert: number;
  trackInventory: boolean;
  /** Whether the row's selling_price is entered inclusive of GST. */
  priceIncludesGst: boolean;
}

export interface ValidationResult {
  rows: ValidatedRow[];
  errors: { row: number; message: string }[];
}

function num(s: string | undefined, def = 0): number {
  if (s == null || s.trim() === '') return def;
  const n = Number(s);
  if (!Number.isFinite(n)) return NaN;
  return n;
}

function bool(s: string | undefined, def = true): boolean {
  if (s == null || s.trim() === '') return def;
  const v = s.trim().toLowerCase();
  if (['false', '0', 'no', 'n'].includes(v)) return false;
  if (['true', '1', 'yes', 'y'].includes(v)) return true;
  return def;
}

export function validateRows(grid: string[][]): ValidationResult {
  const errors: { row: number; message: string }[] = [];
  if (grid.length < 2) {
    return { rows: [], errors: [{ row: 0, message: 'File needs a header row plus at least one data row.' }] };
  }
  const headers = grid[0];
  const map = mapHeaders(headers);
  if (map.name === undefined) {
    errors.push({ row: 0, message: 'Required column "name" not found.' });
  }
  if (map.selling_price === undefined) {
    errors.push({ row: 0, message: 'Required column "selling_price" not found.' });
  }
  if (errors.length > 0) return { rows: [], errors };

  const seenSkus = new Set<string>();
  const rows: ValidatedRow[] = [];
  // Default price-entry mode when the file has no explicit column.
  const defaultInclGst = useSettingsStore.getState().getBoolean('PRODUCT_PRICE_INCLUDES_GST');

  for (let i = 1; i < grid.length; i++) {
    const r = grid[i];
    const get = (key: string) => (map[key] !== undefined ? r[map[key]] : undefined);
    const name = (get('name') ?? '').trim();
    if (!name) { errors.push({ row: i + 1, message: 'Missing name.' }); continue; }

    const sellingPrice = num(get('selling_price'));
    if (!Number.isFinite(sellingPrice) || sellingPrice < 0) {
      errors.push({ row: i + 1, message: `Invalid selling_price "${get('selling_price')}".` });
      continue;
    }

    const sku = (get('sku') ?? '').trim() || null;
    if (sku) {
      if (seenSkus.has(sku)) { errors.push({ row: i + 1, message: `Duplicate SKU "${sku}" within this file.` }); continue; }
      seenSkus.add(sku);
    }

    const purchasePrice = num(get('purchase_price'), 0);
    if (!Number.isFinite(purchasePrice) || purchasePrice < 0) {
      errors.push({ row: i + 1, message: `Invalid purchase_price "${get('purchase_price')}".` });
      continue;
    }
    const mrp = num(get('mrp'), sellingPrice);
    const gstRate = num(get('gst_rate'), 0);
    if (!Number.isFinite(gstRate) || gstRate < 0 || gstRate > 100) {
      errors.push({ row: i + 1, message: `Invalid gst_rate "${get('gst_rate')}" (must be 0-100).` });
      continue;
    }
    const currentStock = num(get('stock'), 0);
    if (!Number.isFinite(currentStock)) {
      errors.push({ row: i + 1, message: `Invalid stock "${get('stock')}".` });
      continue;
    }
    const lowStockAlert = num(get('low_stock_alert'), 0);

    rows.push({
      name,
      sku,
      barcode: (get('barcode') ?? '').trim() || null,
      hsnCode: (get('hsn_code') ?? '').trim() || null,
      categoryName: (get('category') ?? '').trim() || null,
      unit: (get('unit') ?? 'pcs').trim() || 'pcs',
      sellingPrice,
      purchasePrice,
      mrp,
      gstRate,
      currentStock,
      lowStockAlert,
      trackInventory: bool(get('track_inventory'), true),
      priceIncludesGst: bool(get('price_includes_gst'), defaultInclGst),
    });
  }

  return { rows, errors };
}

// ─── Commit ───────────────────────────────────────────────────────────────
export interface CommitResult {
  inserted: number;
  skipped: number;
  errors: { row: number; message: string }[];
}

const CHUNK_SIZE = 200;

export async function commitRows(
  rows: ValidatedRow[],
  context: { storeId: string; merchantId: string },
  onProgress?: (done: number, total: number) => void,
): Promise<CommitResult> {
  const errors: { row: number; message: string }[] = [];
  let inserted = 0;
  let skipped = 0;

  // 1) Load existing SKUs for this store to skip dupes
  const existing = await database.get<Product>('products')
    .query(Q.where('store_id', context.storeId), Q.where('is_deleted', false))
    .fetch();
  const existingSkus = new Set(existing.map((p) => (p.sku || '').trim()).filter((s) => s.length > 0));

  // 2) Load existing categories for this merchant; create missing ones up-front
  const allCats = await database.get<Category>('categories')
    .query(Q.where('merchant_id', context.merchantId), Q.where('is_deleted', false))
    .fetch();
  const catByName = new Map<string, Category>(
    allCats.map((c) => [c.name.trim().toLowerCase(), c]),
  );

  const neededCatNames = Array.from(
    new Set(rows.map((r) => r.categoryName?.trim().toLowerCase()).filter((n): n is string => !!n && !catByName.has(n))),
  );
  if (neededCatNames.length > 0) {
    await txn(async (db) => {
      const collection = db.get<Category>('categories');
      const created = neededCatNames.map((lcName) => {
        const display = rows.find((r) => r.categoryName?.trim().toLowerCase() === lcName)?.categoryName ?? lcName;
        return collection.prepareCreate((c: Category) => {
          c.merchantId = context.merchantId;
          c.name = display;
          c.parentId = null;
          c.sortOrder = 0;
          c.isSynced = false;
          c.version = 1;
          c.isDeleted = false;
        });
      });
      await db.batch(...created);
      created.forEach((c) => catByName.set((c as Category).name.trim().toLowerCase(), c as Category));
    });
  }

  // 3) Filter out duplicates; keep original index for error reporting
  const toInsert: { row: ValidatedRow; sourceIdx: number }[] = [];
  rows.forEach((r, idx) => {
    if (r.sku && existingSkus.has(r.sku)) {
      skipped++;
      errors.push({ row: idx + 2, message: `SKU "${r.sku}" already exists; skipped.` });
      return;
    }
    toInsert.push({ row: r, sourceIdx: idx });
  });

  // 4) Chunked atomic writes
  for (let start = 0; start < toInsert.length; start += CHUNK_SIZE) {
    const chunk = toInsert.slice(start, start + CHUNK_SIZE);
    try {
      const storeInclusive = useSettingsStore.getState().getBoolean('TAX_INCLUSIVE');
      await txn(async (db) => {
        const collection = db.get<Product>('products');
        const ops = chunk.map(({ row }) => {
          const cat = row.categoryName ? catByName.get(row.categoryName.trim().toLowerCase()) ?? null : null;
          return collection.prepareCreate((p: Product) => {
            p.storeId = context.storeId;
            p.categoryId = cat ? cat.id : null;
            p.name = row.name;
            p.sku = row.sku;
            p.barcode = row.barcode;
            p.hsnCode = row.hsnCode;
            p.mrp = row.mrp;
            // Normalise the entered price to the store's billing convention.
            p.sellingPrice = toStoredSellingPrice(row.sellingPrice, row.gstRate, 0, row.priceIncludesGst, storeInclusive);
            p.purchasePrice = row.purchasePrice;
            p.gstRate = row.gstRate;
            p.cessRate = 0;
            p.unit = row.unit;
            p.currentStock = row.currentStock;
            p.lowStockAlert = row.lowStockAlert;
            p.trackInventory = row.trackInventory;
            p.isService = false;
            p.isActive = true;
            p.imageUrl = null;
            p.isSynced = false;
            p.version = 1;
            p.isDeleted = false;
          });
        });
        await db.batch(...ops);
      });
      inserted += chunk.length;
      onProgress?.(inserted + skipped, rows.length);
    } catch (err) {
      chunk.forEach(({ sourceIdx }) => {
        errors.push({ row: sourceIdx + 2, message: `Write failed: ${(err as Error).message}` });
      });
    }
  }

  if (inserted > 0) {
    void logActivity({
      action: 'inventory_imported',
      entityType: 'product',
      entityId: context.storeId,
      storeId: context.storeId,
      changes: {
        summary: `Imported ${inserted} product${inserted === 1 ? '' : 's'}${skipped > 0 ? ` · ${skipped} skipped` : ''}`,
        quantity: inserted,
      },
    });
  }

  return { inserted, skipped, errors };
}
